import React from "react";
import { C } from "../constants";

/** Split a line into text + clickable http(s) URL parts */
function linkifyParts(text) {
  const s = String(text ?? "");
  const re = /(https?:\/\/[^\s<>"'`\[\]{}]+)/gi;
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) parts.push({ type: "text", value: s.slice(last, m.index) });
    let url = m[1];
    let trail = "";
    const trailMatch = url.match(/[.,;:!?)]+$/);
    if (trailMatch) {
      trail = trailMatch[0];
      url = url.slice(0, -trail.length);
    }
    if (url) parts.push({ type: "link", value: url });
    if (trail) parts.push({ type: "text", value: trail });
    last = m.index + m[1].length;
  }
  if (last < s.length) parts.push({ type: "text", value: s.slice(last) });
  return parts.length ? parts : [{ type: "text", value: s }];
}

function LinkedText({ text, style }) {
  return (
    <span style={style}>
      {linkifyParts(text).map((p, i) =>
        p.type === "link" ? (
          <a
            key={i}
            href={p.value}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#2563EB",
              textDecoration: "underline",
              wordBreak: "break-all",
              cursor: "pointer",
            }}
          >
            {p.value}
          </a>
        ) : (
          <React.Fragment key={i}>{p.value}</React.Fragment>
        )
      )}
    </span>
  );
}

/** Detect subheading / label lines that should render bold */
export function isHeadingLine(raw) {
  const t = String(raw || "").trim();
  if (!t) return false;

  const bare = t.replace(/^[▶•\-–—*▸▪\s]+/, "").trim();

  // Known daily/weekly label prefixes (gap-driven + classic labels)
  if (/^(?:[▶•\-–—*▸▪]\s*)?(Week goal|Goal|Goals|Must complete|Complete|Done when|Next week|Build|Project(?:\s*link)?|DSA(?:\s*link)?|System Design|Subjects?|Topic|Sub-?topics?(?:\s+today)?|Learn(?:\s+today)?|Apply|Prove|Process|Log|BUILD HOOK|PROJECT LINK|DT LENS|PROBLEM GLANCE|PROBLEM SLICE|PROBLEM STATEMENT REVIEW|KNOWLEDGE GAP|MICRO-TEACH|DT ACTIVATION|PIPELINE GOAL|CONNECTIVITY|SNS AGENT WORKBENCH|TECH STACK SESSION|Language|Theme|How|Purpose|Target|Rule|Tip|Faculty note|Worked example|Check question|Coding Practice|Product of the Day|AFTER problem review|Your problem|What you will finish today|How it links|Team check-in|Personal check-in|Placement practice|End of day|LEARNED|FINISHED|LINKED|TOMORROW|Important|Company for today|Learn tip|About this topic|Today(?:'s|’s) focus|Key points|Sample data|Sketch|Do now|1\) Concept|2\) Where this appears in your project|3\) How to use it in your project today|Reference links|DT|Placement|Complete DT|System Design notes)\b/i.test(t)) {
    return true;
  }

  // Step A/B/C… pipeline lines
  if (/^(?:[▶•\-–—*▸▪]\s*)?Step\s+[A-Z0-9]\b/i.test(t)) return true;

  // "SubjectName: …" / "DSA (25 min): …" / "Label:"
  if (/^[A-Za-z0-9][^:\n]{0,56}:(\s|$)/.test(bare)) return true;

  // ALL-CAPS short headers / labels
  if (/^[A-Z][A-Z0-9 &\-/()]{2,48}$/.test(bare)) return true;

  // Section-style headers without trailing colon (short, title case, no long sentence)
  if (
    bare.length <= 56 &&
    !/[.?!]$/.test(bare) &&
    /^(Learning|Coding|Project|Placement|Stand.?up|Retro|Refresh|Break|Lunch|System Design|Agent Workbench|Mini Build|Interests|Goals|Capability|Domain|Peer Review|Problem Framing|Problem Lab|Problem Review|Homework)\b/i.test(bare)
  ) {
    return true;
  }

  return false;
}

/** Bold "Label:" prefix when present; otherwise bold whole heading lines. URLs are clickable. */
export function RichLineText({ text, style = {}, done = false }) {
  const line = String(text ?? "").replace(/\\n/g, "\n");
  if (!line.trim()) return null;

  const heading = isHeadingLine(line);
  const base = {
    whiteSpace: "pre-wrap",
    textDecoration: done ? "line-through" : "none",
    color: done ? C.muted : C.text,
    lineHeight: 1.5,
    ...style,
  };

  const m = line.match(/^(\s*)((?:[▶•\-–—*▸▪]\s*)?[^:\n]{1,56}:)(\s*)([\s\S]*)$/);
  if (m && (heading || m[2].trim().length <= 48)) {
    return (
      <span style={base}>
        <span style={{ fontWeight: 700 }}>{m[1]}{m[2]}</span>
        <LinkedText text={`${m[3]}${m[4]}`} style={{ fontWeight: 400 }} />
      </span>
    );
  }

  return <LinkedText text={line} style={{ ...base, fontWeight: heading ? 700 : 400 }} />;
}

/** Multi-line cell with bold subheadings (weekly tables) */
export function RichWeeklyCell({ value }) {
  const text = String(value ?? "—").replace(/\\n/g, "\n");
  if (!text.trim()) return "—";

  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {lines.map((line, i) => (
        <div key={i} style={{ lineHeight: 1.4, fontSize: "0.92em" }}>
          <RichLineText text={line} />
        </div>
      ))}
    </div>
  );
}
